'use client';

import { getProductDiscount } from '@/lib/productDiscounts';
import {
  formatCurrencyAmount,
  formatProductPrice,
  getProductCurrency,
} from '@/lib/products';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  captionVariants,
  slideVariants,
} from './animation/carouselVariants.js';
import './topProducts.scss';

function resolveMessage(messages, key, fallback) {
  return messages?.[key] || fallback;
}

function fillTemplate(template, replacements = {}) {
  return Object.entries(replacements).reduce(
    (value, [key, replacement]) =>
      value.replaceAll(`{{${key}}}`, String(replacement ?? '')),
    String(template || ''),
  );
}

const TopProductsCarouselMotion = ({
  products = [],
  activeCoupons = [],
  customSlides = [],
  messages = {},
  locale,
}) => {
  const [resolvedProducts, setResolvedProducts] = useState(() =>
    Array.isArray(products) ? products : [],
  );
  // useRef (not useState) so toggling it never triggers a re-render / effect
  // cleanup — a state update inside the async fetch would abort its own request.
  const hasRequestedFallbackRef = useRef(false);
  const [paused, setPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isCompactLandscape, setIsCompactLandscape] = useState(false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const router = useRouter();

  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const timerRef = useRef(null);

  // Detect viewport size for responsive shop poster (576px = Bootstrap sm breakpoint)
  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setIsMobile(width < 576);
      setIsCompactLandscape(width > height && height <= 600);
      setIsDesktopViewport(width >= 992);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  useEffect(() => {
    const nextProducts = Array.isArray(products) ? products : [];
    setResolvedProducts(nextProducts);
    // Mark as already-resolved so the fallback fetch never fires when real
    // server-provided products arrive.
    if (nextProducts.length > 0) hasRequestedFallbackRef.current = true;
  }, [products]);

  useEffect(() => {
    if (resolvedProducts.length > 0 || hasRequestedFallbackRef.current) {
      return;
    }

    // Mark synchronously before the async call so a concurrent render can't
    // spawn a second parallel fetch.
    hasRequestedFallbackRef.current = true;

    const controller = new AbortController();
    let cancelled = false;

    const hydrateTopProducts = async () => {
      try {
        const response = await fetch('/api/products/top', {
          headers: {
            accept: 'application/json',
          },
          cache: 'no-store',
          signal: controller.signal,
        });

        if (!response.ok) {
          return;
        }

        const payload = await response.json();

        if (!cancelled && Array.isArray(payload) && payload.length > 0) {
          setResolvedProducts(payload);
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          // Ignore transient client fetch failures and keep custom slides visible.
        }
      }
    };

    hydrateTopProducts();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [resolvedProducts.length]);

  const allSlides = [...customSlides, ...resolvedProducts];
  const total = allSlides.length;
  const handleNavigate = useCallback(
    (delta) => {
      if (!total) return;
      setDirection(delta);
      setIndex((i) => (i + delta + total) % total);
    },
    [total],
  );

  const AUTOPLAY_MS = 6000;
  const startTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => handleNavigate(1), AUTOPLAY_MS);
  }, [handleNavigate]);

  useEffect(() => {
    if (!prefersReducedMotion && !paused && total > 1) startTimer();
    return () => clearTimeout(timerRef.current);
  }, [index, total, paused, prefersReducedMotion, startTimer]);

  const togglePaused = useCallback(() => {
    setPaused((p) => !p);
    if (!paused) clearTimeout(timerRef.current);
    else if (!prefersReducedMotion) startTimer();
  }, [paused, prefersReducedMotion, startTimer]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') handleNavigate(1);
      else if (e.key === 'ArrowLeft') handleNavigate(-1);
      else if (e.key === ' ' || e.key === 'Spacebar') {
        if (document.activeElement?.closest('.top-products-hero')) {
          e.preventDefault();
          togglePaused();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleNavigate, togglePaused]);

  const current = allSlides[index];
  const isCustomSlide = Boolean(current?.slideType);
  const isShopSlide = current?.slideType === 'shop';
  const isClickableCustomSlide = isCustomSlide && Boolean(current?.ctaHref);
  const hasSecondary = !isCustomSlide && Boolean(current?.imagesPaths?.[0]);
  const showSecondaryMedia =
    hasSecondary && (isCompactLandscape || isDesktopViewport);
  const currentCurrency = isCustomSlide ? null : getProductCurrency(current);
  const discountInfo = isCustomSlide
    ? { hasDiscount: false }
    : getProductDiscount(current, activeCoupons);
  const currentPrice = isCustomSlide
    ? null
    : formatProductPrice(current, locale) || current?.price;
  const discountedPrice =
    !isCustomSlide && discountInfo?.hasDiscount
      ? formatCurrencyAmount(
          discountInfo.discountedPrice,
          currentCurrency,
          locale,
        )
      : null;
  const hasPromoPrice =
    Boolean(discountedPrice) && discountedPrice !== currentPrice;
  const savingsLabel = hasPromoPrice
    ? discountInfo.discountType === 'percentage'
      ? fillTemplate(
          resolveMessage(messages, 'savePercentage', 'Save {{value}}%'),
          { value: discountInfo.discountValue },
        )
      : fillTemplate(resolveMessage(messages, 'saveAmount', 'Save {{value}}'), {
          value:
            formatCurrencyAmount(
              discountInfo.discountAmount,
              currentCurrency,
              locale,
            ) || discountInfo.discountAmount,
        })
    : '';

  useEffect(() => {
    if (
      isCustomSlide ||
      !current?.productNumber ||
      typeof router.prefetch !== 'function'
    ) {
      return;
    }

    const adjacentProducts = [
      current,
      allSlides[(index + 1) % total],
      allSlides[(index - 1 + total) % total],
    ].filter((s) => s && !s.slideType && s.productNumber);

    adjacentProducts.forEach((product) => {
      router.prefetch(`/product/${product.productNumber}`);
    });
  }, [current, isCustomSlide, index, allSlides, router, total]);

  useEffect(() => {
    if (resolvedProducts.length) {
      try {
        window.dispatchEvent(new CustomEvent('topproducts:ready'));
      } catch (_) {}
    }
  }, [resolvedProducts.length]);

  const onDragStart = () => {
    setIsDragging(true);
    try {
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    } catch (_) {}
    setPaused(true);
  };

  const onDragEnd = (e, info) => {
    setIsDragging(false);
    try {
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    } catch (_) {}
    const threshold = 60; // px threshold before changing slide
    if (info?.offset?.x < -threshold) handleNavigate(1);
    else if (info?.offset?.x > threshold) handleNavigate(-1);
    setTimeout(() => setPaused(false), 220);
  };

  if (!allSlides.length) {
    return null;
  }

  return (
    <section
      className="top-products-hero position-relative"
      aria-roledescription="carousel"
      aria-label={resolveMessage(messages, 'topProducts', 'Top products')}
    >
      <div className="visually-hidden" aria-live="polite">
        {resolveMessage(
          messages,
          'slideStatus',
          `Slide ${index + 1} of ${total}`,
        )
          .replace('{{index}}', String(index + 1))
          .replace('{{total}}', String(total))}
      </div>

      <div
        className={`carousel-viewport ${showSecondaryMedia ? 'has-secondary' : ''}`}
        style={{ touchAction: 'pan-y' }}
      >
        <AnimatePresence
          initial={false}
          custom={direction}
          mode="popLayout"
          presenceAffectsLayout={!prefersReducedMotion}
        >
          <motion.figure
            key={isCustomSlide ? current.key : current.productNumber}
            className="carousel-slide m-0"
            custom={direction}
            variants={prefersReducedMotion ? undefined : slideVariants}
            initial={prefersReducedMotion ? false : 'enter'}
            animate={prefersReducedMotion ? false : 'center'}
            exit={prefersReducedMotion ? false : 'exit'}
            // enable drag for both mouse and touch
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.22}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            whileTap={{ cursor: 'grabbing' }}
            style={{
              position: 'relative',
              height: '100%',
            }}
          >
            <div
              role={
                isClickableCustomSlide
                  ? 'link'
                  : isCustomSlide
                    ? undefined
                    : 'link'
              }
              tabIndex={
                isClickableCustomSlide ? 0 : isCustomSlide ? undefined : 0
              }
              className={`slide-link d-block w-100 h-100 ${isClickableCustomSlide ? 'cursor-pointer' : ''}`}
              style={{
                position: 'relative',
                display: 'block',
                width: '100%',
                height: '100%',
                cursor: isClickableCustomSlide ? 'pointer' : 'default',
              }}
              onClick={
                isClickableCustomSlide
                  ? (e) => {
                      if (isDragging) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      router.push(current.ctaHref);
                    }
                  : isCustomSlide
                    ? undefined
                    : (e) => {
                        if (isDragging) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        router.push(`/product/${current.productNumber}`);
                      }
              }
              onKeyDown={
                isClickableCustomSlide
                  ? (e) => {
                      if (isDragging) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        router.push(current.ctaHref);
                      }
                    }
                  : isCustomSlide
                    ? undefined
                    : (e) => {
                        if (isDragging) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          router.push(`/product/${current.productNumber}`);
                        }
                      }
              }
            >
              {/* Background image */}
              <div
                className="hero-media-layer hero-media-layer--main"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'block',
                  height: '100%',
                }}
              >
                <Image
                  src={
                    isCustomSlide && isShopSlide && isMobile
                      ? current.bgSrcPortrait ||
                        current.bgSrc ||
                        '/hub/gaming_couple.jpg'
                      : isCustomSlide
                        ? current.bgSrc || '/hub/gaming_couple.jpg'
                        : current.coverImage || '/icons/store.png'
                  }
                  alt={isCustomSlide ? '' : current.name || 'Top Product'}
                  role={isCustomSlide ? 'presentation' : undefined}
                  width={1600}
                  height={1600}
                  sizes={showSecondaryMedia ? '50vw' : '100vw'}
                  className={`hero-img main ${showSecondaryMedia ? 'hero-img--split' : ''} ${isCustomSlide ? 'custom-slide-bg' : ''}`}
                  priority
                  style={{
                    pointerEvents: 'none',
                    objectFit: isCustomSlide
                      ? isShopSlide
                        ? 'fill'
                        : 'cover'
                      : 'contain',
                    top: isCustomSlide && isShopSlide ? 0 : '-2.5rem',
                  }}
                />
              </div>
              {showSecondaryMedia && (
                <div
                  className="hero-media-layer hero-media-layer--secondary"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'block',
                    height: '100%',
                  }}
                >
                  <Image
                    src={
                      current.imagesPaths[0] ||
                      current.coverImage ||
                      '/icons/store.png'
                    }
                    alt=""
                    width={1600}
                    height={1600}
                    sizes="50vw"
                    aria-hidden="true"
                    className="hero-img secondary hero-img--secondary-visible"
                    style={{ pointerEvents: 'none' }}
                  />
                </div>
              )}

              {!isShopSlide && (
                <motion.figcaption
                  variants={prefersReducedMotion ? undefined : captionVariants}
                  initial={prefersReducedMotion ? false : 'hidden'}
                  animate={prefersReducedMotion ? false : 'show'}
                  exit={prefersReducedMotion ? false : 'exit'}
                  className={`hero-caption ${isCustomSlide ? 'hero-caption--custom' : ''}`}
                  style={{ pointerEvents: 'auto' }}
                >
                  {isCustomSlide ? (
                    /* ── Custom slide caption (Hub) ── */
                    <div className="custom-slide-caption">
                      <div className="custom-slide-copy">
                        {current.eyebrow ? (
                          <p
                            className={`custom-slide-eyebrow custom-slide-eyebrow--${current.tone || 'primary'}`}
                          >
                            {current.eyebrow}
                          </p>
                        ) : null}
                        <p className="custom-slide-title">{current.title}</p>
                        {current.description ? (
                          <p className="custom-slide-desc">
                            {current.description}
                          </p>
                        ) : null}
                      </div>
                      <div className="caption-row">
                        {current.ctaHref ? (
                          <Link
                            href={current.ctaHref}
                            className={`custom-slide-cta btn btn-${current.tone || 'primary'}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {current.ctaLabel || 'Discover →'}
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    /* ── Product slide caption ── */
                    <>
                      <div className="caption-main">
                        <p className="hero-name mb-1 caption-line1">
                          {resolveMessage(
                            messages,
                            'product_name',
                            '{{name}}',
                          ).replace('{{name}}', current.name || '')}
                        </p>
                        <div
                          className="hero-price-stack caption-line2 price--landscape"
                          aria-hidden="true"
                        >
                          {hasPromoPrice ? (
                            <>
                              <p className="hero-price hero-price--discounted mb-0">
                                {discountedPrice}
                              </p>
                              <div className="hero-price-meta">
                                <span className="hero-price hero-price--original">
                                  {currentPrice}
                                </span>
                                {savingsLabel ? (
                                  <span className="hero-price-badge">
                                    {savingsLabel}
                                  </span>
                                ) : null}
                              </div>
                            </>
                          ) : (
                            <p className="hero-price mb-0">{currentPrice}</p>
                          )}
                        </div>
                      </div>

                      <div className="caption-row">
                        <div className="hero-price-stack price--portrait">
                          {hasPromoPrice ? (
                            <>
                              <p className="hero-price hero-price--discounted mb-0">
                                {discountedPrice}
                              </p>
                              <div className="hero-price-meta">
                                <span className="hero-price hero-price--original">
                                  {currentPrice}
                                </span>
                                {savingsLabel ? (
                                  <span className="hero-price-badge">
                                    {savingsLabel}
                                  </span>
                                ) : null}
                              </div>
                            </>
                          ) : (
                            <p className="hero-price mb-0">{currentPrice}</p>
                          )}
                        </div>
                        {total > 1 && (
                          <div
                            className="carousel-controls d-flex align-items-center gap-2 flex-wrap"
                            style={{ pointerEvents: 'auto' }}
                          >
                            <Button
                              variant="outline-light"
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleNavigate(-1);
                              }}
                              aria-label={resolveMessage(
                                messages,
                                'prevSlide',
                                'Previous slide',
                              )}
                            >
                              ‹
                            </Button>
                            <span className="carousel-status small text-white">
                              {resolveMessage(
                                messages,
                                'slideStatus',
                                `Slide ${index + 1} of ${total}`,
                              )
                                .replace('{{index}}', String(index + 1))
                                .replace('{{total}}', String(total))}
                            </span>
                            <Button
                              variant="outline-light"
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleNavigate(1);
                              }}
                              aria-label={resolveMessage(
                                messages,
                                'nextSlide',
                                'Next slide',
                              )}
                            >
                              ›
                            </Button>
                            <Button
                              variant="outline-light"
                              size="sm"
                              aria-pressed={paused}
                              aria-label={
                                paused
                                  ? resolveMessage(
                                      messages,
                                      'play',
                                      'Play autoplay',
                                    )
                                  : resolveMessage(
                                      messages,
                                      'pause',
                                      'Pause autoplay',
                                    )
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                togglePaused();
                              }}
                            >
                              {paused ? '▶' : '⏸'}
                            </Button>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {total > 1 && !isCustomSlide && (
                    <div
                      className="carousel-dots d-flex justify-content-center align-self-center gap-2 mt-2"
                      role="tablist"
                      aria-label={resolveMessage(
                        messages,
                        'topProducts',
                        'Top products',
                      )}
                      style={{ pointerEvents: 'auto' }}
                    >
                      {allSlides.map((slide, i) => (
                        <button
                          key={
                            slide.slideType ? slide.key : slide.productNumber
                          }
                          role="tab"
                          aria-selected={i === index}
                          aria-label={resolveMessage(
                            messages,
                            'slideStatus',
                            `Slide ${i + 1} of ${total}`,
                          )
                            .replace('{{index}}', String(i + 1))
                            .replace('{{total}}', String(total))}
                          tabIndex={i === index ? 0 : -1}
                          className={`carousel-dot ${i === index ? 'active' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isDragging) handleNavigate(i - index);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </motion.figcaption>
              )}
            </div>
          </motion.figure>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default TopProductsCarouselMotion;
